"use client";

import {useEffect, useRef} from "react";
import {useArtifactPanel} from "@/contexts/chat/ArtifactPanelContext";
import {useGailDock} from "@/contexts/chat/GailDockContext";

/**
 * Messages that reach a chat from outside its composer: workspace-panel
 * outcomes, the dock's queued handoff note, and `?input=` / `?prefill=` in
 * the URL. Each waits until the chat can take a turn.
 */
export function useExternalChatSends({
   conversationId,
   fillContainer,
   canSend,
   tokenLimitReached,
   sendMessage,
   setInputValue,
}: {
   conversationId: string;
   /** The chat is the dock's, which owns handoff notes. */
   fillContainer?: boolean;
   /** The conversation, its assistant, and the composer are ready for a turn. */
   canSend: boolean;
   tokenLimitReached: boolean;
   sendMessage: (text: string) => Promise<boolean>;
   setInputValue: (text: string) => void;
}) {
   // Ref-bridged because the chat's send callback is recreated each render.
   const sendMessageRef = useRef(sendMessage);
   sendMessageRef.current = sendMessage;

   // Let workspace-panel actions (a sibling of this chat view) post outcomes
   // into the conversation, e.g. "I saved the script as ...".
   const {registerChatMessageSender} = useArtifactPanel();
   useEffect(() => {
      registerChatMessageSender((text) => {
         void sendMessageRef.current(text);
      });
      return () => registerChatMessageSender(null);
   }, [registerChatMessageSender]);

   // A script save hands the conversation to the dock, then this surface sends
   // the save note. Sending it on the full-page chat would abort when that
   // view unmounts during the route change. Wait until the dock can actually
   // send, and leave the note queued if it can't yet.
   const {sendDockFollowUp} = useGailDock();
   useEffect(() => {
      if (!fillContainer || !canSend || tokenLimitReached) return;
      // useChat reports send failures; keep the note queued if a sender rejects.
      void sendDockFollowUp(conversationId, (text) => sendMessageRef.current(text)).catch(() => {});
   }, [fillContainer, canSend, tokenLimitReached, conversationId, sendDockFollowUp]);

   const hasResumedFromQueryRef = useRef(false);
   useEffect(() => {
      if (hasResumedFromQueryRef.current || typeof window === "undefined" || !canSend) return;

      const params = new URLSearchParams(window.location.search);
      const input = params.get("input");
      const prefill = params.get("prefill");
      if (!input && !prefill) return;

      hasResumedFromQueryRef.current = true;
      const url = new URL(window.location.href);
      url.searchParams.delete("input");
      url.searchParams.delete("prefill");
      window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);

      if (input?.trim()) {
         void sendMessageRef.current(input);
         return;
      }
      if (prefill) setInputValue(prefill);
   }, [canSend, setInputValue]);
}
